import { useEffect } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/queryKeys'
import i18n from './index'

/**
 * **One page, two languages — and the one invalidation that closes it (IN-37).**
 *
 * Changing the UI language flips i18next instantly and entirely client-side. Every string
 * the FRONTEND owns re-renders in the new language on the spot. Strings the SERVER rendered
 * do not: they arrived in a response, they sit in a TanStack cache entry behind a
 * `staleTime`, and nothing about a language change tells that cache it is now wrong. So the
 * nav, the tabs and the group headings turn German while the sentences inside them stay
 * English — confirmed on staging in both directions, and cleared by a re-login, which is
 * exactly the signature of a cache-lifetime bug rather than a resolution bug.
 *
 * `useUiLocaleSync` states the invariant this file exists to keep: *"Two sides reading the
 * same facts in the same order cannot disagree about what language the app is in."* That is
 * true of the two RESOLUTION chains and was never true of the CACHE between them. F5 and F40
 * were resolution breaks; this is the third occurrence and the first of a different kind.
 *
 * ## Why a listener on i18next rather than a call in the language dropdown
 *
 * The dropdown is not the only writer. `useUiLocaleSync` also calls `changeLanguage` — on
 * first load, and again whenever the preferences query answers with something other than
 * what the browser detector guessed. A fix wired only into Settings leaves that path
 * two-languaged, and leaves the next `changeLanguage` call site to rediscover the bug. The
 * event is the one place every writer already goes through.
 *
 * ## What is in the list, and what is deliberately not
 *
 * The list is "responses whose TEXT the server rendered in the caller's UI locale, and which
 * a client caches across a language change". A sweep of every `MessageSource` call site in
 * `src/main/java` on 2026-09-09 found exactly one such surface — the attention panel, under
 * its two keys. The near misses, so the next reader does not re-do the sweep:
 *
 *   - **Bell notifications** (`NotificationServiceImpl`, `InquiryNotificationService`) render
 *     their text at CREATION time and store it on the row (`n.setMessage(...)`). Refetching
 *     returns the same frozen sentence, so an invalidation here would be pure noise — that
 *     row is a historical record of what was said, and changing language cannot rewrite it.
 *   - **Emails, push and the attention digest** are rendered at send time and never re-read.
 *   - **Error messages** (`GlobalExceptionHandler`) are resolved per request and not cached.
 *   - **The VAT return export** renders in the STUDIO's locale, not the caller's, so a
 *     member's own language switch does not change a byte of it.
 *
 * A new endpoint that renders user-facing text server-side in the caller's locale belongs in
 * `SERVER_RENDERED_TEXT_KEYS` in the same commit that adds it.
 *
 * @see i18n/useUiLocaleSync.ts — the resolution chain this keeps the cache in step with
 */

/**
 * The keys whose CONTENT is server-rendered in the caller's UI locale.
 *
 * <p>Not a `cacheGroups.ts` group: a group is "one endpoint read under several keys", and
 * `queryKeyCacheGroups.test.ts` rejects one that is neither (it fails a group whose endpoint
 * is not actually aliased, and one with fewer than two members). These two keys are the
 * opposite shape — two DIFFERENT endpoints, `GET /attention` and
 * `GET /attention/project/{id}`, one key each, related by what renders them rather than by
 * what they return. The shared-with-`invalidateShootAndCalendar` shape is the right one: a
 * named "this changed" helper that says which surfaces it reaches and why.
 */
export const SERVER_RENDERED_TEXT_KEYS = {
  /** `GET /attention` — the dashboard panel and the standalone `/attention` page. */
  attentionPanel: queryKeys.dashboard.attention,
  /** `GET /attention/project/{id}` — the same store, one project (F200). Matched for EVERY id. */
  projectAttention: queryKeys.projects.projectAttention,
} as const

/** True for `['projects', <any id>, 'attention']` — every project's panel, whichever is cached. */
function isProjectAttentionKey(key: readonly unknown[]): boolean {
  return key.length === 3 && key[0] === 'projects' && key[2] === 'attention'
}

/**
 * **The language changed — every server-rendered sentence on screen is now the wrong one.**
 *
 * Awaitable, so a test (or a caller that wants to sequence something after) can wait for the
 * refetches to be in flight.
 */
export function invalidateServerRenderedText(qc: QueryClient): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: SERVER_RENDERED_TEXT_KEYS.attentionPanel }),
    // By predicate, not by key: the project panel is per-id and any number of them may be
    // cached. `['projects']` would reach them but would also invalidate every other project
    // query in the app, which a language change has no business doing.
    qc.invalidateQueries({ predicate: (q) => isProjectAttentionKey(q.queryKey) }),
  ]).then(() => undefined)
}

/**
 * Mount once, inside the authenticated tree, beside {@link useUiLocaleSync}. Public
 * client-facing pages are deliberately not covered: they live outside `AppLayout`, they set
 * their language from the document's own locale via `usePublicLocale`, and they have no
 * long-lived cache to go stale across a switch.
 */
export function useServerRenderedTextRefresh() {
  const qc = useQueryClient()

  useEffect(() => {
    const onLanguageChanged = () => {
      void invalidateServerRenderedText(qc)
    }
    i18n.on('languageChanged', onLanguageChanged)
    return () => {
      i18n.off('languageChanged', onLanguageChanged)
    }
  }, [qc])
}
